import React, { useState, useContext, useCallback, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import ContactListDirect from "../../components/ContactListDirect";
import DirectTicket from "../../components/DirectTicket";

import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { AuthContext } from "../../context/Auth/AuthContext";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const contactsPanelWidth = 380;

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    flex: 1,
    padding: "2px",
    height: `calc(100% - 48px)`,
    overflowY: "hidden",
  },
  chatPaper: {
    display: "flex",
    height: "100%",
  },
  contactsPanel: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflowY: "hidden",
    width: `${contactsPanelWidth}px`,
    minWidth: `${contactsPanelWidth}px`,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  messagesWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    flexGrow: 1,
    position: "relative",
  },
  welcomeMsg: {
    background: theme.palette.tabHeaderBackground,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    textAlign: "center",
    flexDirection: "column",
    gap: "16px",
  },
  welcomeText: {
    color: theme.palette.text.secondary,
    fontSize: "14px",
    maxWidth: "400px",
    lineHeight: 1.6,
  },
  logo: {
    content: "url(" + (theme.mode === "light"
      ? theme.calculatedLogoLight()
      : theme.calculatedLogoDark()) + ")",
    maxWidth: "200px",
    opacity: 0.7,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
}));

const DirectChat = () => {
  const classes = useStyles();
  const history = useHistory();
  const { ticketId } = useParams();
  const { user } = useContext(AuthContext);

  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleContactSelect = useCallback((contact, ticket) => {
    setSelectedContact(contact);
    if (ticket?.uuid) {
      history.push(`/direct-chat/${ticket.uuid}`);
      return;
    }
    // No ticket in list - search for existing ticket for this contact
    (async () => {
      setLoading(true);
      try {
        // Search tickets by contact number to find existing ones
        const { data: searchResult } = await api.get("/tickets", {
          params: {
            searchParam: contact.number,
            showAll: "true",
            pageNumber: 1,
          },
        });

        // Find any ticket for this exact contact
        const existing = searchResult?.tickets?.find(
          (t) => t.contactId === contact.id
        );

        if (existing) {
          history.push(`/direct-chat/${existing.uuid}`);
        } else {
          // Really no ticket - create one
          const { data: newTicket } = await api.post("/tickets", {
            contactId: contact.id,
            userId: user.id,
            status: "open",
          });
          history.push(`/direct-chat/${newTicket.uuid}`);
        }
      } catch (err) {
        if (err?.response?.status === 403 && err?.response?.data?.ticket) {
          history.push(`/direct-chat/${err.response.data.ticket.uuid}`);
        } else {
          toastError(err);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user.id, history]);

  return (
    <QueueSelectedProvider>
      <div className={classes.chatContainer}>
        <div className={classes.chatPaper}>
          <div className={classes.contactsPanel}>
            <ContactListDirect
              onContactSelect={handleContactSelect}
              selectedContactId={selectedContact?.id}
            />
          </div>
          <div className={classes.messagesWrapper}>
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress />
              </div>
            ) : ticketId ? (
              <DirectTicket key={ticketId} />
            ) : (
              <Hidden only={["sm", "xs"]}>
                <Paper square variant="outlined" className={classes.welcomeMsg}>
                <img className={classes.logo} alt="logo" />
                <Typography className={classes.welcomeText}>
                  Selecione um contato para iniciar uma conversa direta.
                  As mensagens ficam salvas independente de fila ou status.
                </Typography>
              </Paper>
            </Hidden>
          )}
          </div>
        </div>
      </div>
    </QueueSelectedProvider>
  );
};

export default DirectChat;
